import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Add CSS for spinner animation
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the styles
if (!document.getElementById('spinner-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'spinner-styles';
  styleSheet.textContent = spinnerStyles;
  document.head.appendChild(styleSheet);
}

function renderImageFromTag(imgTag) {
  const match = imgTag.match(/src=\"([^\"]+)\".*width=\"(\d+)\".*height=\"(\d+)\"/);
  if (match) {
    return <img src={match[1]} width={match[2]} height={match[3]} alt="Image" style={{ border: '1px solid #ccc' }} />;
  }
  return null;
}

function renderVideoFromUrl(url) {
  if (/vimeo\.com/.test(url)) {
    const id = url.split('/').pop();
    return (
      <div className="video-container">
        <iframe src={`https://player.vimeo.com/video/${id}`} width="800" height="450" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="Vimeo Video" />
      </div>
    );
  }
  if (/youtube\.com|youtu\.be/.test(url)) {
    let id = url;
    if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1];
    else if (url.includes('v=')) id = url.split('v=')[1].split('&')[0];
    return (
      <div className="video-container">
        <iframe width="800" height="450" src={`https://www.youtube.com/embed/${id}`} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen title="YouTube Video" />
      </div>
    );
  }
  return (
    <div className="video-container">
      <video src={url} width="800" height="450" controls />
    </div>
  );
}

const AssignmentViewer = forwardRef(({
  assignment, mode = 'preview', interactive = false, instructorChoices, onSurveySubmit, onSurveyCancel, registrationId, eventDetails, enrollmentId, projectId, projectType, userData, projectData, onClose, onSubmit, submission, onGradingUpdate, showAnswers = false, allowGrading = false, hideSubmitButton = false, submitButtonText, customSubmit = false, resubmitSubmissionId = null
}, ref) => {
  // Debug: log props on mount
  React.useEffect(() => {
    if (mode === 'survey') {
      console.log('[AssignmentViewer] Survey mode props:', { assignment, registrationId });
    }
  }, [mode, assignment, registrationId]);

  const [openAnswers, setOpenAnswers] = useState({});
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(''); // 'submitting', 'success', 'validation-error'
  const [showValidationErrors, setShowValidationErrors] = useState(false); // Track if validation errors should be shown
  const [selectedFiles, setSelectedFiles] = useState({}); // Store selected files by question ID (not uploaded yet)
  const [uploadedFileUrls, setUploadedFileUrls] = useState({}); // Store uploaded file URLs for viewing existing submissions
  const [uploadingFiles, setUploadingFiles] = useState({}); // Track upload progress by question ID
  const [assignmentSubmissionId, setAssignmentSubmissionId] = useState(null); // Store the UUID for file organization
  const [directionsExpanded, setDirectionsExpanded] = useState(true); // Track if directions section is expanded
  
  // --- Grading State ---
  const isGradeMode = mode === 'grade' && submission && (submission.graderesult === 'not-required' || submission.graderesult === 'needsEvaluation' || (typeof submission.graderesult === 'string' && submission.graderesult.includes('%')));
  // For initial grading, default hours to assignment.hours
  const isInitialGrading = isGradeMode && submission && submission.graderesult === 'needsEvaluation';
  
  // Show answers in previewWithAnswers mode or grade mode
  const shouldShowAnswers = mode === 'previewWithAnswers' || isGradeMode;
  const [gradeHours, setGradeHours] = useState(() => {
    if (isInitialGrading && assignment && assignment.hours) return assignment.hours;
    return submission ? submission.hoursreceived ?? '' : '';
  });
  const [gradePoints, setGradePoints] = useState(() => {
    if (!submission || !submission.submissiondata) return {};
    let data = typeof submission.submissiondata === 'string' ? JSON.parse(submission.submissiondata) : submission.submissiondata;
    const points = {};
    data.forEach(q => {
      if (typeof q.maxPoints === 'number') {
        points[q._id] = q.pointsreceived ?? '';
      }
    });
    return points;
  });
  const [gradeError, setGradeError] = useState('');
  const [gradeSubmitting, setGradeSubmitting] = useState(false);
  
  // Check if this is a Survey assignment
  const isSurveyAssignment = assignment && assignment.gradingresulttype === 'Survey';

  /** Per-question rows from the saved submission (for earned points in view mode). */
  const submissionQuestionById = React.useMemo(() => {
    if (!submission?.submissiondata) return {};
    let data = submission.submissiondata;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return {};
      }
    }
    if (!Array.isArray(data)) return {};
    const map = {};
    for (const row of data) {
      if (row && row._id) map[row._id] = row;
    }
    return map;
  }, [submission]);
  
  // Helper: find if any questions need points (only for non-Survey assignments)
  const questionsNeedingPoints = React.useMemo(() => {
    if (isSurveyAssignment || !assignment || !submission || !submission.submissiondata) return [];
    let data = typeof submission.submissiondata === 'string' ? JSON.parse(submission.submissiondata) : submission.submissiondata;
    return data.filter((q, idx) => typeof q.maxPoints === 'number' && q.type !== 'directions' && q.type !== 'video' && (gradePoints[q._id] === undefined || gradePoints[q._id] === null || gradePoints[q._id] === ''));
  }, [assignment, submission, gradePoints, isSurveyAssignment]);
  
  // Helper: sum of pointsreceived and maxPoints (only for non-Survey assignments)
  const pointsSummary = React.useMemo(() => {
    if (isSurveyAssignment || !assignment || !submission || !submission.submissiondata) return { total: 0, max: 0 };
    let data = typeof submission.submissiondata === 'string' ? JSON.parse(submission.submissiondata) : submission.submissiondata;
    let total = 0, max = 0;
    for (const q of data) {
      if (typeof q.maxPoints === 'number' && q.type !== 'directions' && q.type !== 'video') {
        total += Number(gradePoints[q._id] ?? 0);
        max += Number(q.maxPoints);
      }
    }
    return { total, max };
  }, [assignment, submission, gradePoints, isSurveyAssignment]);

  // Initialize responses from existing submission if provided
  useEffect(() => {
    if (submission && submission.submissiondata) {
      console.log('Submission data received:', submission.submissiondata);
      let submissionData = submission.submissiondata;
      
      // Handle case where submissiondata might be a JSON string
      if (typeof submissionData === 'string') {
        try {
          submissionData = JSON.parse(submissionData);
        } catch (e) {
          console.error('Error parsing submission data:', e);
          return;
        }
      }
      
      console.log('Parsed submission data:', submissionData);
      
      if (Array.isArray(submissionData)) {
        const initialResponses = {};
        const initialOpenAnswers = {};
        
        submissionData.forEach(questionData => {
          console.log('Processing question data:', questionData);
          if (questionData.response !== undefined && questionData.response !== null) {
            if (questionData.type === 'openAnswer' && questionData.responseType !== 'Number-Float') {
              initialOpenAnswers[questionData._id] = questionData.response;
            } else if (questionData.type === 'upload') {
              // For upload questions, store the URLs for viewing
              setUploadedFileUrls(prev => ({ ...prev, [questionData._id]: questionData.response }));
            } else {
              initialResponses[questionData._id] = questionData.response;
            }
          }
        });
        
        console.log('Initial responses:', initialResponses);
        console.log('Initial open answers:', initialOpenAnswers);
        
        setResponses(initialResponses);
        setOpenAnswers(initialOpenAnswers);
      }
    }
  }, [submission]);

  // Reset grading state when submission or assignment changes
  useEffect(() => {
    // Reset grade hours
    if (isInitialGrading && assignment && assignment.hours) {
      setGradeHours(assignment.hours);
    } else {
      setGradeHours(submission ? submission.hoursreceived ?? '' : '');
    }
    
    // Reset grade points
    if (!submission || !submission.submissiondata) {
      setGradePoints({});
    } else {
      let data = typeof submission.submissiondata === 'string' ? JSON.parse(submission.submissiondata) : submission.submissiondata;
      const points = {};
      data.forEach(q => {
        if (typeof q.maxPoints === 'number') {
          points[q._id] = q.pointsreceived ?? '';
        }
      });
      setGradePoints(points);
    }
    
    // Reset error state
    setGradeError('');
    setGradeSubmitting(false);
  }, [submission, assignment, isInitialGrading]);

  // Generate assignmentSubmissionId when component mounts for new submissions or resubmit
  useEffect(() => {
    if (resubmitSubmissionId && interactive) {
      setAssignmentSubmissionId(resubmitSubmissionId);
    } else if (!submission && interactive && mode === 'submit') {
      setAssignmentSubmissionId(uuidv4());
    }
  }, [submission, interactive, mode, resubmitSubmissionId]);
  
  // Expose validateSurvey and getSurveyData to parent via ref (always call, not conditionally)
  useImperativeHandle(ref, () => ({
    validateSurvey: () => validateSurvey(),
    getSurveyData: () => {
      if (!assignment) return [];
      return assignment.questions.map(q => {
        const questionData = {
          _id: q._id,
          label: q.label,
          type: q.type,
          required: q.required,
          gradingType: q.gradingType || 'none',
          choices: q.choices,
          answer: q.answer || ''
        };
        // Add response based on question type
        if (q.type === 'singleChoice' || q.type === 'multipleChoice') {
          questionData.response = responses[q._id] || '';
        } else if (q.type === 'openAnswer') {
          if (q.responseType === 'Number-Float') {
            questionData.response = responses[q._id] || '';
            questionData.responseType = 'Number-Float';
          } else {
            questionData.response = openAnswers[q._id] || '';
          }
        }
        return questionData;
      });
    }
  }));
  
  if (!assignment) return <div>Loading...</div>;

  const validateSurvey = () => {
    const requiredQuestions = assignment.questions.filter(q => 
      q.required && q.type !== 'video' && q.type !== 'directions'
    );
    
    for (const question of requiredQuestions) {
      if (question.type === 'singleChoice' || question.type === 'multipleChoice') {
        if (!responses[question._id]) {
          return `Please complete question ${assignment.questions.indexOf(question) + 1}`;
        }
      } else if (question.type === 'openAnswer') {
        if (question.responseType === 'Number-Float') {
          if (!responses[question._id] || responses[question._id].trim() === '') {
            return `Please complete question ${assignment.questions.indexOf(question) + 1}`;
          }
        } else {
          if (!openAnswers[question._id] || openAnswers[question._id].trim() === '') {
            return `Please complete question ${assignment.questions.indexOf(question) + 1}`;
          }
        }
      } else if (question.type === 'upload') {
        // Check if required upload questions have files selected
        if (!selectedFiles[question._id] || selectedFiles[question._id].length === 0) {
          return `Please select files for question ${assignment.questions.indexOf(question) + 1}`;
        }
      }
    }
    return null; // All valid
  };

  // Helper function to check if a question is incomplete
  const isQuestionIncomplete = (question) => {
    if (!question.required || question.type === 'video' || question.type === 'directions') {
      return false;
    }
    
    if (question.type === 'singleChoice') {
      return !responses[question._id];
    } else if (question.type === 'multipleChoice') {
      // For multiple choice, check if the array is empty or doesn't exist
      return !responses[question._id] || !Array.isArray(responses[question._id]) || responses[question._id].length === 0;
    } else if (question.type === 'openAnswer') {
      if (question.responseType === 'Number-Float') {
        return !responses[question._id] || responses[question._id].trim() === '';
      } else {
        return !openAnswers[question._id] || openAnswers[question._id].trim() === '';
      }
    } else if (question.type === 'upload') {
      return !selectedFiles[question._id] || selectedFiles[question._id].length === 0;
    }
    
    return false;
  };

  const handleResponseChange = (questionId, response) => {
    setResponses(prev => ({ ...prev, [questionId]: response }));
  };

  const handleFileSelection = (questionId, files) => {
    if (!files || files.length === 0) return;

    // Store the selected files (not uploaded yet)
    setSelectedFiles(prev => ({ 
      ...prev, 
      [questionId]: Array.from(files).map((file, index) => ({
        file: file,
        originalName: file.name,
        fileIndex: index
      }))
    }));
  };

  const removeFile = (questionId, fileIndex) => {
    setSelectedFiles(prev => {
      const updatedFiles = { ...prev };
      if (updatedFiles[questionId]) {
        updatedFiles[questionId] = updatedFiles[questionId].filter((_, index) => index !== fileIndex);
        if (updatedFiles[questionId].length === 0) {
          delete updatedFiles[questionId];
        }
      }
      return updatedFiles;
    });
  };

  const handleSubmit = async () => {
    // Determine if this is a survey submission or regular assignment submission
    if (mode === 'survey' && registrationId) {
      // Handle survey submission (existing logic)
      await handleSurveySubmit();
    } else if (mode === 'submit') {
      // For custom component flow, bypass project checks and backend POST
      if (customSubmit) {
        await handleAssignmentSubmit(true);
      } else if (enrollmentId && projectId) {
        // Handle regular assignment submission
        await handleAssignmentSubmit(false);
      } else {
        console.error('Missing required parameters for submission');
        return;
      }
    } else {
      console.error('Missing required parameters for submission');
      return;
    }
  };

  const handleSurveySubmit = async () => {
    if (!registrationId) {
      console.error('No registration ID provided for survey submission');
      return;
    }

    setSubmitting(true);
    setSubmissionStatus('submitting');

    // Validate the survey
    const validationError = validateSurvey();
    if (validationError) {
      setSubmissionStatus('validation-error');
      setTimeout(() => {
        setSubmitting(false);
        setSubmissionStatus('');
      }, 1000);
      return;
    }

    try {
      // Prepare survey data with responses
      const surveyData = assignment.questions.map(q => {
        const questionData = {
          _id: q._id,
          label: q.label,
          type: q.type,
          required: q.required,
          gradingType: q.gradingType || 'none',
          choices: q.choices,
          answer: q.answer || ''
        };

        // Add response based on question type
        if (q.type === 'singleChoice' || q.type === 'multipleChoice') {
          questionData.response = responses[q._id] || '';
        } else if (q.type === 'openAnswer') {
          if (q.responseType === 'Number-Float') {
            questionData.response = responses[q._id] || '';
            questionData.responseType = 'Number-Float';
          } else {
            questionData.response = openAnswers[q._id] || '';
          }
        }

        return questionData;
      });

      const token = localStorage.getItem('token');
      await axios.put(`/api/feeventregistrations/${registrationId}/survey`, {
        surveydata: surveyData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Success - show success message and return to My Hours
      setSubmissionStatus('success');
      setTimeout(() => {
        if (onSurveySubmit) {
          onSurveySubmit();
        }
      }, 1500);

    } catch (error) {
      console.error('Error submitting survey:', error);
      setSubmissionStatus('error');
      setTimeout(() => {
        setSubmitting(false);
        setSubmissionStatus('');
      }, 1500);
    }
  };

  const handleAssignmentSubmit = async (isCustomFlow = false) => {
    if (!isCustomFlow) {
      if (!enrollmentId || !projectId) {
        console.error('Missing enrollment ID or project ID for assignment submission');
        return;
      }
      if (!projectType) {
        console.error('Missing project type for assignment submission');
        return;
      }
    }

    setSubmitting(true);
    setSubmissionStatus('submitting');
    setShowValidationErrors(false); // Clear previous validation errors when starting new submission

    // Validate the assignment
    const validationError = validateSurvey(); // Reuse survey validation logic
    if (validationError) {
      setSubmissionStatus('validation-error');
      setShowValidationErrors(true); // Show validation errors and keep them visible
      setTimeout(() => {
        setSubmitting(false);
        setSubmissionStatus('');
        // Don't clear showValidationErrors here - keep it until next validation
      }, 1000);
      return;
    }

    try {
      // First, upload all selected files
      const uploadedFiles = {};
      setUploadingFiles({}); // Reset upload progress

      for (const [questionId, files] of Object.entries(selectedFiles)) {
        if (files && files.length > 0) {
          setUploadingFiles(prev => ({ ...prev, [questionId]: true }));
          
          const uploadedUrls = [];
          for (let i = 0; i < files.length; i++) {
            const fileData = files[i];
            const file = fileData.file;
            const fileExtension = file.name.split('.').pop();
            const fileName = `${questionId}_${fileData.fileIndex}.${fileExtension}`;
            
            // Generate a unique upload ID for this file
            const uploadId = uuidv4();
            
            // Construct the S3 key to maintain folder organization
            const key = `public/assignment-submissions/${resubmitSubmissionId || assignmentSubmissionId}/${fileName}`;
            
            // Get presigned URL for direct S3 upload
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/upload-url', {
              filename: file.name,
              contentType: file.type,
              fileSize: file.size,
              uploadId: uploadId,
              details: 'Assignment submission file',
              key: key
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            // Upload file directly to S3 using presigned URL
            const uploadResponse = await fetch(response.data.presignedUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': file.type
              }
            });
            
            if (!uploadResponse.ok) {
              throw new Error(`Upload failed: ${uploadResponse.statusText}`);
            }

            uploadedUrls.push(response.data.uploadRecord.link);
          }
          
          uploadedFiles[questionId] = uploadedUrls;
          setUploadingFiles(prev => ({ ...prev, [questionId]: false }));
        }
      }

      // Prepare assignment submission data with uploaded file URLs
      const submissionData = assignment.questions.map(q => {
        const questionData = {
          _id: q._id,
          label: q.label,
          type: q.type,
          required: q.required,
          maxPoints: q.maxPoints,
          choices: q.choices,
          answer: q.answer || ''
        };

        // Add response based on question type
        if (q.type === 'singleChoice' || q.type === 'multipleChoice') {
          questionData.response = responses[q._id] || '';
        } else if (q.type === 'openAnswer') {
          if (q.responseType === 'Number-Float') {
            questionData.response = responses[q._id] || '';
            questionData.responseType = 'Number-Float';
          } else {
            questionData.response = openAnswers[q._id] || '';
          }
        } else if (q.type === 'upload') {
          // For upload questions, include the uploaded files data
          questionData.response = uploadedFiles[q._id] || [];
        }
        // No pointsreceived logic here; backend will handle it
        return questionData;
      });

      if (!isCustomFlow) {
        const token = localStorage.getItem('token');
        if (resubmitSubmissionId) {
          await axios.put(`/api/assignmentsubmissions/${resubmitSubmissionId}/resubmit`, {
            submissiondata: submissionData,
            assignmentSubmissionId: resubmitSubmissionId || assignmentSubmissionId,
            userData: userData,
            projectData: projectData,
            assignmentData: assignment
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          // Find the current projectsubmission updateddate if available
          let projectsubmissionUpdatedDate = null;
          if (submission && submission.projectsubmission && submission.updateddate) {
            projectsubmissionUpdatedDate = submission.updateddate;
          } else if (projectData && projectData.existingProjectSubmission && projectData.existingProjectSubmission.updateddate) {
            projectsubmissionUpdatedDate = projectData.existingProjectSubmission.updateddate;
          }
          await axios.post('/api/assignmentsubmissions', {
            enrollment: enrollmentId,
            submissiondata: submissionData,
            project: projectId,
            assignment: assignment.id,
            projectType: projectType,
            userData: userData,
            projectData: projectData,
            assignmentData: assignment,
            assignmentSubmissionId: assignmentSubmissionId,
            projectsubmissionUpdatedDate
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }

      // Compute requiresManual and autoGrade summary for parent consumers
      const requiresManual = Array.isArray(assignment.questions) && assignment.questions.some(q => q.type === 'openAnswer' && q.responseType !== 'Number-Float' || q.type === 'upload');
      let autoScore = 0;
      let autoMax = 0;
      if (Array.isArray(assignment.questions)) {
        assignment.questions.forEach(q => {
          if (q.type === 'singleChoice') {
            autoMax += 1;
            const selected = responses[q._id];
            const correct = q.answer === '' || q.answer === selected;
            if (correct) autoScore += 1;
          } else if (q.type === 'multipleChoice' && Array.isArray(q.answer)) {
            autoMax += 1;
            const selected = Array.isArray(responses[q._id]) ? responses[q._id] : [];
            const correct = q.answer.length === 0 || (Array.isArray(selected) && q.answer.every(a => selected.includes(a)) && selected.every(a => q.answer.includes(a)));
            if (correct) autoScore += 1;
          } else if (q.type === 'trueFalse') {
            autoMax += 1;
            const selected = responses[q._id];
            const correct = String(q.answer).toLowerCase() === String(selected).toLowerCase();
            if (correct) autoScore += 1;
          }
        });
      }
      const autoGrade = autoMax > 0 ? { score: autoScore, maxScore: autoMax } : null;

      // Success - show success message and notify parent
      setSubmissionStatus('success');
      setShowValidationErrors(false); // Clear validation errors on success
      setTimeout(() => {
        if (onSubmit) {
          try {
            onSubmit({
              gradingResultType: assignment?.gradingresulttype,
              requiresManual,
              autoGrade,
              assignmentId: assignment?.id,
              submissionData,
              // Pass the ID used for upload path so parent can create assignmentsubmissions with same ID
              assignmentSubmissionId: resubmitSubmissionId || assignmentSubmissionId
            });
          } catch {}
        }
        // Close overlay and reset state after notifying parent
        setSubmitting(false);
        setSubmissionStatus('');
      }, 1500);

    } catch (error) {
      console.error('Error submitting assignment:', error);
      setSubmissionStatus('error');
      setTimeout(() => {
        setSubmitting(false);
        setSubmissionStatus('');
      }, 1500);
    }
  };

  // --- Grading Update Handler ---
  const handleGradeUpdate = async () => {
    setGradeError('');
    setGradeSubmitting(true);
    
    // For Survey assignments, skip points validation and calculation
    if (isSurveyAssignment) {
      try {
        const token = localStorage.getItem('token');
        await axios.patch(`/api/assignmentsubmissions/${submission.id}`, {
          hoursreceived: gradeHours,
          pointsreceived: {}, // Empty object for Survey assignments
          graderesult: 'not-required' // Survey assignments are always not-required
        }, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          setGradeError('Updated!');
          // Update local submission object so UI reflects new values
          if (submission) {
            submission.hoursreceived = gradeHours;
            submission.graderesult = 'not-required';
          }
        });
        if (onGradingUpdate) onGradingUpdate();
        setTimeout(() => setGradeError(''), 2000);
      } catch (err) {
        setGradeError('Failed to update.');
      } finally {
        setGradeSubmitting(false);
      }
      return;
    }
    
    // Validate points (only for non-Survey assignments)
    if (submission && submission.submissiondata) {
      let data = typeof submission.submissiondata === 'string' ? JSON.parse(submission.submissiondata) : submission.submissiondata;
      // Restriction: For Points assignments, all questions with maxPoints must have a non-null, non-empty pointsreceived
      if (assignment.gradingresulttype === 'Points') {
        for (const q of data) {
          if (typeof q.maxPoints === 'number' && q.type !== 'directions' && q.type !== 'video') {
            const val = gradePoints[q._id];
            if (val === undefined || val === null || val === '') {
              setGradeError('All questions must have points assigned before updating.');
              setGradeSubmitting(false);
              return;
            }
            // (1) Validate no question.pointsreceived > question.maxPoints
            let qIdx = 1;
            if (assignment && Array.isArray(assignment.questions)) {
              qIdx = assignment.questions.findIndex(qq => qq._id === q._id) + 1;
            }
            if (Number(val) > Number(q.maxPoints)) {
              setGradeError(`Points for question #${qIdx} cannot exceed max points (${q.maxPoints}).`);
              setGradeSubmitting(false);
              return;
            }
          }
        }
      }
      for (const q of data) {
        if ((q.type === 'upload' || (q.type === 'openAnswer' && q.responseType !== 'Number-Float')) && typeof q.maxPoints === 'number') {
          const val = Number(gradePoints[q._id] ?? 0);
          if (val < 0 || val > q.maxPoints) {
            setGradeError(`Points for question #${assignment.questions.findIndex(qq => qq._id === q._id) + 1} must be between 0 and ${q.maxPoints}`);
            setGradeSubmitting(false);
            return;
          }
        }
      }
    }
    // (2) Conditional hoursreceived logic
    let hoursToSend = gradeHours;
    let percentString = undefined;
    if (assignment.gradingresulttype === 'Points' && submission && submission.graderesult === 'needsEvaluation') {
      // Calculate total points and max points
      let data = typeof submission.submissiondata === 'string' ? JSON.parse(submission.submissiondata) : submission.submissiondata;
      let totalPoints = 0;
      let maxPoints = 0;
      for (const q of data) {
        if (typeof q.maxPoints === 'number' && q.type !== 'directions' && q.type !== 'video') {
          const val = Number(gradePoints[q._id] ?? 0);
          totalPoints += val;
          maxPoints += Number(q.maxPoints);
        }
      }
      if (maxPoints > 0) {
        const percentNumber = (totalPoints / maxPoints) * 100;
        percentString = percentNumber.toFixed(1) + '%';
        if (percentNumber >= 70) {
          // Allow hoursreceived as entered
          hoursToSend = gradeHours;
        } else {
          // Set hoursreceived to 0
          hoursToSend = 0;
        }
      }
    }
    // If graderesult includes %, allow hoursreceived to be set to any value entered (already default)
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/assignmentsubmissions/${submission.id}`, {
        hoursreceived: hoursToSend,
        pointsreceived: gradePoints,
        graderesult: percentString // Always send the calculated percentage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setGradeError('Updated!');
        // Update local submission object so UI reflects new values
        if (submission) {
          submission.hoursreceived = hoursToSend;
          if (submission.submissiondata) {
            let data = typeof submission.submissiondata === 'string' ? JSON.parse(submission.submissiondata) : submission.submissiondata;
            for (const q of data) {
              if (gradePoints[q._id] !== undefined && typeof q.maxPoints === 'number' && q.type !== 'directions' && q.type !== 'video') {
                q.pointsreceived = Number(gradePoints[q._id]);
              }
            }
            submission.submissiondata = data;
          }
          // Always update graderesult to our calculated percentage string
          if (percentString) {
            submission.graderesult = percentString;
          }
        }
      });
      if (onGradingUpdate) onGradingUpdate();
      setTimeout(() => setGradeError(''), 2000);
    } catch (err) {
      setGradeError('Failed to update.');
    } finally {
      setGradeSubmitting(false);
    }
  };
  // --- End Grading Update Handler ---

  // --- Grading Summary Bar ---
  let gradeSummary = null;
  if (!isSurveyAssignment && submission && typeof submission.graderesult === 'string' && submission.graderesult.includes('%')) {
    // Extract percentage as number
    const percentMatch = submission.graderesult.match(/([\d.]+)%/);
    const percent = percentMatch ? parseFloat(percentMatch[1]) : null;
    // Calculate points
    let totalPoints = 0, maxPoints = 0;
    let data = typeof submission.submissiondata === 'string' ? JSON.parse(submission.submissiondata) : submission.submissiondata;
    for (const q of data) {
      if (typeof q.maxPoints === 'number' && q.type !== 'directions' && q.type !== 'video') {
        maxPoints += Number(q.maxPoints);
        totalPoints += Number(q.pointsreceived ?? 0);
      }
    }
    gradeSummary = (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        fontWeight: 700,
        fontSize: 28,
        marginBottom: 8,
        color: percent !== null && percent >= 70 ? '#11AF07' : '#E93C11',
        gap: 16
      }}>
        <span style={{ fontSize: 18, color: '#222', fontWeight: 500 }}>{totalPoints}/{maxPoints} points</span>
        <span style={{ fontSize: 32 }}>{percent !== null ? `${percent}%` : submission.graderesult}</span>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          /* Global question number styling */
          .question-number {
            background: none !important;
            border-radius: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            display: inline !important;
            box-shadow: none !important;
            border: none !important;
          }
          
          /* Global ReactQuill container styling */
          .ql-container {
            max-width: 100% !important;
            overflow: hidden !important;
            height: 200px !important;
            min-height: 200px !important;
          }
          
          .ql-editor {
            max-width: 100% !important;
            overflow-wrap: break-word !important;
            word-wrap: break-word !important;
            height: 200px !important;
            min-height: 200px !important;
            overflow-y: auto !important;
          }
          
          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            .assignment-viewer-container {
              max-width: 100% !important;
              margin: 20px auto !important;
              padding: 16px !important;
              border-radius: 8px !important;
            }
            
            .assignment-title {
              font-size: 24px !important;
              line-height: 1.3 !important;
            }
            
            .directions-header {
              font-size: 18px !important;
              flex-direction: row !important;
              align-items: center !important;
              gap: 8px !important;
            }
            
            .directions-button {
              font-size: 14px !important;
              padding: 6px 12px !important;
            }
            
            .questions-header {
              font-size: 18px !important;
            }
            
            .question-item {
              padding: 16px !important;
              margin-bottom: 16px !important;
              overflow: hidden !important;
              word-wrap: break-word !important;
            }
            
            .question-number {
              font-size: 14px !important;
              left: 12px !important;
              top: 16px !important;
              opacity: 0.7 !important;
              font-weight: 500 !important;
              background: none !important;
              border-radius: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
              display: inline !important;
            }
            
            .question-content {
              margin-left: 24px !important;
              overflow: hidden !important;
              max-width: 100% !important;
            }
            
            .choice-list {
              padding-left: 0 !important;
            }
            
            .choice-item {
              padding: 8px 0 !important;
            }
            
            .choice-item label {
              font-size: 14px !important;
              gap: 8px !important;
            }
            
            .choice-item input {
              width: 16px !important;
              height: 16px !important;
            }
            
            .submit-buttons {
              flex-direction: column !important;
              gap: 12px !important;
              padding: 0 16px !important;
            }
            
            .submit-button, .cancel-button {
              width: 100% !important;
              padding: 14px 24px !important;
              font-size: 16px !important;
            }
            
            .video-container {
              width: 100% !important;
              height: auto !important;
              max-width: 100% !important;
            }
            
            .video-container iframe,
            .video-container video {
              width: 100% !important;
              height: auto !important;
              max-width: 100% !important;
            }
            
            .directions-content {
              font-size: 14px !important;
              line-height: 1.5 !important;
            }
            
            .directions-content a {
              font-size: 14px !important;
            }
            
            /* Points display styling for mobile */
            .points-display {
              font-size: 11px !important;
              padding: 1px 6px !important;
              right: 16px !important;
              top: 16px !important;
            }
            
            /* Rich text editor mobile styling */
            .ql-editor {
              max-width: 100% !important;
              overflow-wrap: break-word !important;
              word-wrap: break-word !important;
              height: 200px !important;
              min-height: 200px !important;
              overflow-y: auto !important;
            }
            
            .ql-container {
              max-width: 100% !important;
              overflow: hidden !important;
              height: 200px !important;
              min-height: 200px !important;
            }
            
            .ql-toolbar {
              max-width: 100% !important;
              overflow: hidden !important;
            }
          }
        `}
      </style>
      {/* Answers Notice for previewWithAnswers mode - Moved outside main container */}
      {mode === 'previewWithAnswers' && (
        <div style={{
          maxWidth: 900,
          margin: '40px auto 0 auto',
          padding: 16,
          backgroundColor: '#ffebee',
          borderRadius: 8,
          border: '1px solid #f44336',
          borderLeft: '4px solid #f44336'
        }}>
          <div style={{ fontWeight: 600, color: '#c62828', marginBottom: 4 }}>
            DO NOT SHARE THIS LINK WITH STUDENTS
          </div>
          <div style={{ fontSize: 14, color: '#d32f2f' }}>
            This assignment is being displayed in preview mode with answers shown. Correct answers are highlighted in green.
          </div>
        </div>
      )}
      
      <div 
        className="assignment-viewer-container"
        style={{
          maxWidth: 900,
          margin: '40px auto',
          padding: 32,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 4px 24px 0 rgba(60,72,88,0.10), 0 1.5px 4px 0 rgba(60,72,88,0.08)',
          border: '1px solid #f0f0f0',
          position: 'relative'
        }}
      >
        {/* Overlay for submission state */}
        {submitting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(2px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px auto'
            }}></div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
              {submissionStatus === 'submitting' && (mode === 'survey' ? 'Submitting your survey...' : 'Submitting your assignment...')}
              {submissionStatus === 'success' && (mode === 'survey' ? '✅ Survey submitted successfully!' : '✅ Assignment submitted successfully!')}
              {submissionStatus === 'validation-error' && 'Please complete all required questions'}
              {submissionStatus === 'error' && 'Error occurred. Please try again.'}
            </div>
          </div>
        </div>
      )}

      {/* Event Details Header for Survey Mode */}
      {mode === 'survey' && eventDetails && (
        <div style={{
          marginBottom: 24,
          padding: 16,
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#495057' }}>Event Details</h3>
          <div style={{ fontSize: 14, color: '#6c757d' }}>
            <div><strong>Title:</strong> {eventDetails.title}</div>
            <div><strong>Date:</strong> {new Date(eventDetails.date).toLocaleDateString()}</div>
            {eventDetails.start_time && eventDetails.end_time && (
              <div><strong>Time:</strong> {eventDetails.start_time} - {eventDetails.end_time}</div>
            )}
            {eventDetails.location && (
              <div><strong>Location:</strong> {eventDetails.location}</div>
            )}
          </div>
        </div>
      )}

      {/* Grading Summary Bar */}
      {gradeSummary}

      <h2 className="assignment-title" style={{ marginTop: 0, marginBottom: 24 }}>{assignment.title}</h2>
      
      {/* Grading Controls (grade mode only, moved to top) */}
      {isGradeMode && allowGrading && (
        <div style={{ marginBottom: 24, padding: 16, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
          <h3 style={{ margin: '0 0 12px 0', color: '#1976d2' }}>Grading Controls</h3>
          {/* Points summary - only show for non-Survey assignments */}
          {!isSurveyAssignment && (
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#1D4E89' }}>
              Points: {pointsSummary.total} / {pointsSummary.max}
              {pointsSummary.max > 0 && (
                <span style={{ marginLeft: 8, color: '#1976d2', fontWeight: 500 }}>
                  ({((pointsSummary.total / pointsSummary.max) * 100).toFixed(1)}%)
                </span>
              )}
            </div>
          )}
          {/* Notices - only show for non-Survey assignments */}
          {!isSurveyAssignment && questionsNeedingPoints.length > 0 && (
            <div style={{ color: '#d32f2f', marginBottom: 8, fontWeight: 500 }}>
              Some questions still need you to assign points
            </div>
          )}
          {!isSurveyAssignment && submission && submission.graderesult === 'needsEvaluation' && (
            <div style={{ color: '#1976d2', marginBottom: 8, fontWeight: 500 }}>
              Student must get over 70% or higher to receive hours for this initial grading.
            </div>
          )}
          {!isSurveyAssignment && submission && typeof submission.graderesult === 'string' && submission.graderesult.includes('%') && (
            <div style={{ color: '#388e3c', marginBottom: 8, fontWeight: 500 }}>
              You may override the hours received here by clicking update regardless of student's percentage on this assignment
            </div>
          )}
          {/* Survey assignment notice */}
          {isSurveyAssignment && (
            <div style={{ color: '#1976d2', marginBottom: 8, fontWeight: 500 }}>
              Survey assignments do not require point-based grading. You may adjust the hours received below.
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 500, marginRight: 8 }}>{isInitialGrading ? 'Possible Hours:' : 'Hours Received:'}</label>
            <input
              type="number"
              value={gradeHours}
              min={0}
              onChange={e => setGradeHours(e.target.value)}
              style={{ width: 80, padding: 4, borderRadius: 4, border: '1px solid #ccc' }}
              disabled={gradeSubmitting}
            />
          </div>
          <button
            onClick={handleGradeUpdate}
            disabled={gradeSubmitting}
            style={{ padding: '8px 24px', fontWeight: 600, background: '#1976d2', color: 'white', border: 'none', borderRadius: 6, cursor: gradeSubmitting ? 'not-allowed' : 'pointer', marginTop: 8 }}
          >
            {isInitialGrading ? 'Set Grade' : 'Update'}
          </button>
          {gradeError && <div style={{ color: gradeError === 'Updated!' ? 'green' : 'red', marginTop: 8 }}>{gradeError}</div>}
        </div>
      )}
      
      {/* Initial Directions Section */}
      {assignment.initialdirections && assignment.initialdirections.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 
            className="directions-header"
            style={{ 
              margin: '0 0 16px 0', 
              color: '#495057', 
              fontSize: '20px', 
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span style={{ opacity: directionsExpanded ? 1 : 0 }}>Directions</span>
            <button
              className="directions-button"
              onClick={() => setDirectionsExpanded(!directionsExpanded)}
              style={{
                background: 'none',
                border: 'none',
                color: '#6c757d',
                fontSize: '14px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.color = '#495057'}
              onMouseLeave={(e) => e.target.style.color = '#6c757d'}
            >
              {directionsExpanded ? 'Hide' : 'View Directions'}
            </button>
          </h3>
          
                      {directionsExpanded && (
              <div style={{ 
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <style>
                  {`
                    .directions-content a {
                      color: #007bff !important;
                      text-decoration: underline !important;
                    }
                    .directions-content a:hover {
                      color: #0056b3 !important;
                      text-decoration: underline !important;
                    }
                  `}
                </style>
                {assignment.initialdirections.map((d, i) => (
                  <div key={d._id || i} style={{ marginBottom: 16 }}>
                    {d.element === 'richText' && (
                      <div 
                        className="directions-content rich-text-display"
                        dangerouslySetInnerHTML={{ __html: d.value }} 
                      />
                    )}
                    {d.element === 'image' && renderImageFromTag(d.value)}
                    {d.element === 'video' && renderVideoFromUrl(d.value)}
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
      
      {/* Questions Section */}
      {assignment.questions && assignment.questions.length > 0 && (
        <div style={{ 
          marginBottom: 32,
          padding: '20px',
          backgroundColor: 'transparent',
          borderRadius: '8px',
          border: '1px solid transparent'
        }}>
          <h3 className="questions-header" style={{ 
            margin: '0 0 16px 0', 
            color: '#495057', 
            fontSize: '20px', 
            fontWeight: '600'
          }}>
            Questions
            {assignment.gradingresulttype === 'Points' && (
              <span style={{ 
                fontSize: '14px', 
                color: '#28a745', 
                fontWeight: '500',
                marginLeft: '8px'
              }}>
                ({assignment.maxpoints || 0} points)
              </span>
            )}
          </h3>
          
                    <ol style={{ paddingLeft: 0, margin: 0 }}>
            <style>
              {`
                .directions-content a {
                  color: #007bff !important;
                  text-decoration: underline !important;
                }
                .directions-content a:hover {
                  color: #0056b3 !important;
                  text-decoration: underline !important;
                }
              `}
            </style>
            {assignment.questions && assignment.questions.map((q, idx) => {
          // Special handling for choices
          let choices = q.choices;
          let choiceValues = q.choices;
          if (!Array.isArray(choices) && typeof choices === 'string') {
            if (choices === '${involvedTrainingPlanInstructors}' && Array.isArray(instructorChoices)) {
              choices = instructorChoices.map(instructor => instructor.name);
              choiceValues = instructorChoices.map(instructor => instructor.account_id);
            } else {
              choices = [];
              choiceValues = [];
            }
          }
          const isIncomplete = showValidationErrors && isQuestionIncomplete(q);
          // Find pointsreceived for this question (grade mode)
          const pointsValue = isGradeMode && typeof q.maxPoints === 'number' ? (gradePoints[q._id] ?? '') : (q.pointsreceived ?? '');
          const subQRow = submissionQuestionById[q._id];
          const prSaved = subQRow?.pointsreceived;
          const earnedPts =
            prSaved !== null && prSaved !== undefined && prSaved !== '' && Number.isFinite(Number(prSaved)) ? Number(prSaved) : null;
          const pointsBadgeLabel =
            typeof q.maxPoints === 'number' && q.maxPoints > 0 && earnedPts !== null
              ? `${earnedPts}/${q.maxPoints} pts`
              : typeof q.maxPoints === 'number' && q.maxPoints > 0
                ? `${q.maxPoints} pts`
                : '';
          return (
            <li key={q._id || idx} className="question-item" style={{
              listStyle: 'none',
              marginBottom: 32,
              background: '#fafbfc',
              borderRadius: 12,
              boxShadow: '0 2px 8px 0 rgba(60,72,88,0.07)',
              padding: 24,
              border: isIncomplete ? '2px solid #dc3545' : '1px solid #f0f0f0',
              position: 'relative',
            }}>
              <span className="question-number" style={{ fontWeight: 700, fontSize: 18, marginRight: 12, color: '#1976d2', position: 'absolute', left: 16, top: 24 }}>
                {idx + 1}.
                {isIncomplete && (
                  <span style={{ 
                    color: '#dc3545', 
                    marginLeft: '8px', 
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    ⚠️
                  </span>
                )}
              </span>
              <div className="question-content" style={{ marginLeft: 32 }}>
              {/* Points input for grading (all but directions/video) - only for non-Survey assignments */}
              {isGradeMode && allowGrading && !isSurveyAssignment && typeof q.maxPoints === 'number' && q.type !== 'directions' && q.type !== 'video' && (
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500, marginRight: 8 }}>Points:</label>
                  <input
                    type="number"
                    value={gradePoints[q._id] ?? ''}
                    min={0}
                    max={q.maxPoints}
                    onChange={e => setGradePoints(p => ({ ...p, [q._id]: e.target.value }))}
                    style={{ width: 60, padding: 4, borderRadius: 4, border: '1px solid #ccc' }}
                    disabled={gradeSubmitting}
                  />
                  <span style={{ marginLeft: 6, color: '#555' }}>out of {q.maxPoints}</span>
                </div>
              )}
              {/* Points display for non-grade mode - only for non-Survey assignments */}
              {!isGradeMode && !allowGrading && !isSurveyAssignment && pointsBadgeLabel && (
                <span className="points-display" style={{
                  position: 'absolute',
                  top: 18,
                  right: 24,
                  color: '#1976d2',
                  fontWeight: 600,
                  fontSize: 15,
                  background: '#e3f0ff',
                  borderRadius: 6,
                  padding: '2px 10px',
                }}>{pointsBadgeLabel}</span>
              )}
              {/* Required indicator for incomplete questions */}
              {isIncomplete && q.required && (
                <span style={{
                  position: 'absolute',
                  top: 18,
                  right: isIncomplete && !isGradeMode && !allowGrading && typeof q.maxPoints === 'number' && q.maxPoints > 0 ? 80 : 24,
                  color: '#dc3545',
                  fontWeight: 600,
                  fontSize: 12,
                  background: '#f8d7da',
                  borderRadius: 6,
                  padding: '2px 8px',
                  border: '1px solid #f5c6cb'
                }}>REQUIRED</span>
              )}
              {/* Validation error message for incomplete questions */}
              {isIncomplete && (
                <div style={{
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#721c24',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  ⚠️ This question is required and must be completed before submitting.
                </div>
              )}
              {/* Directions/Video */}
              {q.type === 'directions' && (
                <div 
                  className="directions-content rich-text-display"
                  dangerouslySetInnerHTML={{ __html: q.label }} 
                />
              )}
              {q.type === 'video' && (
                <div>
                  <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                  {renderVideoFromUrl(q.answer)}
                </div>
              )}
              {/* Single Choice (show answer in grade mode) */}
              {q.type === 'singleChoice' && (
                <div>
                  <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                  <ul className="choice-list" style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                    {choices.map((c, i) => {
                      // For singleChoice: q.answer is a string, if empty string then any choice is correct
                      // Only show correct indicators for non-Survey assignments in previewWithAnswers mode or grade mode
                      const isCorrect = (!isSurveyAssignment && shouldShowAnswers && (q.answer === "" || q.answer === c));
                      const isSelected = responses[q._id] === choiceValues[i];
                      return (
                        <li key={i} className="choice-item" style={{ marginBottom: 8, background: isCorrect ? '#e0ffe0' : undefined, borderRadius: 6, padding: '6px 0' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: interactive ? 'pointer' : 'default', fontSize: 16, gap: 10 }}>
                            <input
                              type="radio"
                              name={`singleChoice_${q._id || idx}`}
                              value={choiceValues[i]}
                              checked={isSelected}
                              onChange={(e) => handleResponseChange(q._id, e.target.value)}
                              style={{ marginRight: 12, width: 18, height: 18 }}
                              disabled={!interactive}
                              tabIndex={interactive ? 0 : -1}
                            />
                            <span>{c}</span>
                            {isCorrect && (
                              <span style={{ color: 'green', marginLeft: 10, fontWeight: 500 }}>(Correct)</span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {/* Multiple Choice (show answers in grade mode) */}
              {q.type === 'multipleChoice' && (
                <div>
                  <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                  <ul className="choice-list" style={{ listStyle: 'none', paddingLeft: 0 }}>
                    {choices.map((c, i) => {
                      // For multipleChoice: q.answer is an array, if empty array then any choice is correct
                      // Only show correct indicators for non-Survey assignments in previewWithAnswers mode or grade mode
                      const isCorrect = (!isSurveyAssignment && shouldShowAnswers && (Array.isArray(q.answer) && (q.answer.length === 0 || q.answer.includes(c))));
                      const isSelected = Array.isArray(responses[q._id]) && responses[q._id].includes(choiceValues[i]);
                      return (
                        <li key={i} className="choice-item" style={{ marginBottom: 4, background: isCorrect ? '#e0ffe0' : undefined, borderRadius: 4, padding: '2px 0' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: interactive ? 'pointer' : 'default', fontSize: 16, gap: 10 }}>
                            <input 
                              type="checkbox" 
                              name={`multipleChoice${q._id}`} 
                              value={choiceValues[i]}
                              checked={isSelected}
                              onChange={(e) => {
                                const currentResponses = Array.isArray(responses[q._id]) ? responses[q._id] : [];
                                if (e.target.checked) {
                                  // Add the selected value
                                  handleResponseChange(q._id, [...currentResponses, choiceValues[i]]);
                                } else {
                                  // Remove the unselected value
                                  handleResponseChange(q._id, currentResponses.filter(val => val !== choiceValues[i]));
                                }
                              }}
                              style={{ marginRight: 12, width: 18, height: 18 }} 
                              disabled={!interactive}
                              tabIndex={interactive ? 0 : -1}
                            />
                            <span>{c}</span>
                            {isCorrect && (
                              <span style={{ color: 'green', marginLeft: 10, fontWeight: 500 }}>(Correct)</span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {/* Open Answer */}
              {q.type === 'openAnswer' && (
                <div>
                  <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                  {q.responseType === 'Number-Float' ? (
                    <input 
                      type="number" 
                      step="any" 
                      value={responses[q._id] || ''}
                      onChange={(e) => handleResponseChange(q._id, e.target.value)}
                      style={{ width: 200, padding: 4, backgroundColor: interactive ? '#fff' : '#f0f0f0' }} 
                      disabled={!interactive} 
                    />
                  ) : (
                    interactive ? (
                                             <ReactQuill
                         theme="snow"
                         value={openAnswers[q._id || idx] || ''}
                         onChange={val => setOpenAnswers(ans => ({ ...ans, [q._id || idx]: val }))}
                         readOnly={false}
                         style={{ height: 200, marginBottom: 8, backgroundColor: '#fff', borderRadius: 8 }}
                       />
                    ) : (
                                             <div 
                         className="rich-text-display"
                         dangerouslySetInnerHTML={{ __html: openAnswers[q._id || idx] || '' }} 
                         style={{ 
                           minHeight: 200, 
                           padding: 16, 
                           backgroundColor: '#f0f0f0', 
                           borderRadius: 8, 
                           border: '1px solid #ddd',
                           marginBottom: 8,
                           lineHeight: '1.6'
                         }}
                       />
                    )
                  )}
                  {/* Show expected answer in previewWithAnswers mode */}
                  {shouldShowAnswers && q.answer && (
                    <div style={{ 
                      marginTop: 8, 
                      padding: 12, 
                      backgroundColor: '#e8f5e9', 
                      border: '1px solid #4caf50', 
                      borderRadius: 6,
                      borderLeft: '4px solid #4caf50'
                    }}>
                      <div style={{ fontWeight: 600, color: '#2e7d32', marginBottom: 4 }}>
                        Expected Answer:
                      </div>
                      <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.answer }} />
                    </div>
                  )}
                </div>
              )}
              {/* Upload */}
              {q.type === 'upload' && (
                <div>
                  <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                  <div style={{ marginBottom: 12 }}>
                    <input 
                      type="file" 
                      multiple
                      disabled={!interactive || uploadingFiles[q._id]}
                      onChange={(e) => handleFileSelection(q._id, e.target.files)}
                      style={{ 
                        width: '100%', 
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: interactive ? '#fff' : '#f0f0f0'
                      }}
                    />
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    </div>
                  </div>
                  
                  {/* Show selected files */}
                  {selectedFiles[q._id] && selectedFiles[q._id].length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Selected Files:</div>
                      {selectedFiles[q._id].map((fileData, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          backgroundColor: '#f9f9f9'
                        }}>
                          <span style={{ fontSize: 14 }}>{fileData.originalName}</span>
                          {interactive && (
                            <button
                              type="button"
                              onClick={() => removeFile(q._id, index)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#d32f2f',
                                cursor: 'pointer',
                                fontSize: 16,
                                padding: '2px 6px'
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show upload progress */}
                  {uploadingFiles[q._id] && (
                    <div style={{ 
                      marginTop: 8, 
                      padding: '8px', 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '4px',
                      textAlign: 'center'
                    }}>
                      Uploading files...
                    </div>
                  )}
                  
                  {/* Show uploaded files for existing submissions */}
                  {uploadedFileUrls[q._id] && uploadedFileUrls[q._id].length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Uploaded Files:</div>
                      {uploadedFileUrls[q._id].map((fileUrl, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          backgroundColor: '#f9f9f9'
                        }}>
                          <span style={{ fontSize: 14 }}>File {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => window.open(fileUrl, '_blank')}
                            style={{
                              background: '#1976d2',
                              border: 'none',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: 12,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontWeight: '500'
                            }}
                          >
                            View File
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              </div>
            </li>
          );
        })}
      </ol>
        </div>
      )}
      
      {/* Submit and Cancel Buttons for Interactive Mode */}
      {interactive && (mode === 'survey' || mode === 'submit') && (
        <div className="submit-buttons" style={{ marginTop: 32, textAlign: 'center', display: 'flex', gap: 16, justifyContent: 'center' }}>
          {(onSurveyCancel || onClose) && (
            <button
              className="cancel-button"
              onClick={mode === 'survey' ? onSurveyCancel : onClose}
              disabled={submitting}
              style={{
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: '600',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
          )}
          {!hideSubmitButton && (
          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '12px 32px',
              fontSize: '16px',
              fontWeight: '600',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
            }}
          >
            {submitButtonText ? submitButtonText : (mode === 'survey' ? 'Submit Survey' : 'Submit Assignment')}
          </button>
          )}
        </div>
      )}
    </div>
    </>
  );
});

export default AssignmentViewer; 